"use client"

/** @format */
import { useEffect, useState, useMemo, useCallback, useRef } from "react"
import ChatWindow from "../components/ChatWindow"
import ChatSidebar from "../components/ChatSidebar"
import { Card, CardContent } from "../components/ui/card"
import { Button } from "../components/ui/button"
import { Menu, X, MailOpen } from "lucide-react"
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet"
import { useSession } from "../context/SessionContext"
import { useTranslation } from "react-i18next"
import i18n from "../i18n" // Import i18n instance
import { supabase } from "../supabaseClient"
import ErrorPopup from "../components/ErrorPopup"
import ReassignDialog from "../components/ReasignDialog"
import ResolveDialog from "../components/ResolveDialogNew"
import MessageInputSection from "../components/MessageInputSection"
import EmptyState from "../components/EmptyState"
import { LoadingData } from "../components/LoadingData"
import ConversationGroupList from "../components/ConverstionGroupList"
import UserHeaderSection from "../components/UserHeaderSection"
import { useConversationHistory, useNotificationFlags, useAiBlock, useReassign } from "../lib/useChatPage"
import { shouldRenderMessage } from "../components/MessageInput";

interface ChatPageProps {
  selectedUserPhone: string | null
}

const ChatPage = () => {
  const { t } = useTranslation()
  const {
    session,
    myUsers,
    activePhone,
    setActivePhone,
    setIsTransferNotification,
    setViewingNotifications,
    language,
    returnControlToAi,
    setReturnControlToAi,
    activeNotification,
    fetchPendingTransfers,
    fetchAllUsersInfo,
    isHistoryPage,
    selectedConversationId,
    setSelectedConversationId,
    setIsHistoryPage,
    selectedConversationLastDate,
    setSelectedConversationLastDate,
    userInfo,
    allAssistants,
    setUserInfo,
    setActiveNotification,
    clearTransferNotification,
    notifications,
    sidebarAction,
    setSidebarAction,
    company
  } = useSession()

  const [dateFilter, setDateFilter] = useState<{ mode: "single" | "range"; from?: string; to?: string }>({
    mode: "single",
  });

  const selectedUserIdRef = useRef<string | number | null>(null);

  const {
    messages,
    setMessages,
    userConversations,
    conversationCounts,
    setConversationCounts,
    usersWithHistory,
    loadingUsersWithHistory,
    selectedUserData,
    setSelectedUserData,
    fetchMessageHistory,
    setSelectedConversationId: internalSetConversationId,
    setSelectedConversationLastDate: internalSetLastDate,
    setUsersWithHistory,
    error,
    setError,
    handleSendMessage,
  } = useConversationHistory({
    activePhone,
    isHistoryPage,
    myUsers,
    session,
    setActivePhone,
    setSelectedConversationId,
    setSelectedConversationLastDate,
    dateFilter,
  })

  const { resolved, handleResolvedChange, handleIgnoredChange } = useNotificationFlags({
    activeNotification,
    session,
    t,
    userInfo,
    fetchPendingTransfers,
    fetchAllUsersInfo,
    setUserInfo,
    setReturnControlToAi,
    setActivePhone,
    clearTransferNotification,
    setActiveNotification,
    setSelectedConversationId,
    notifications,
    allAssistants
  })

  useEffect(() => {
    if (!sidebarAction) return;
  
    if (sidebarAction.type === "resolve") {
      setResolveDialogOpen(true);
    }
    if (sidebarAction.type === "ignore") {
      handleIgnoredChange(true, company);
    }
    if (sidebarAction.type === "reassign") {
      setDialogOpen(true); 
    }
  
    setSidebarAction?.(null);
  }, [sidebarAction, handleIgnoredChange, setSidebarAction]);
  
  

  const { aiBlock, toggleAiBlock, aiUpdating } = useAiBlock({
    selectedUserData,
    session,
    refreshTokenIfNeeded: async () => {},
    setError,
  })

  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [expandedGroups, setExpandedGroups] = useState<{
    [key: string]: boolean
  }>({
    Hoy: true,
    Ayer: false,
    "Últimos 7 días": false,
    "Últimos 30 días": false,
  })
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedAssistantId, setSelectedAssistantId] = useState<string | null>(null)
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false)
  const [closureContext, setClosureContext] = useState("")
  const [reassignNote, setReassignNote] = useState("")
  const [isLoadingConversation, setIsLoadingConversation] = useState(false)

  const { reassignNotification } = useReassign({
    activeNotification,
    session,
    allAssistants,
    userInfo,
    selectedAssistantId,
    reassignNote,
    fetchPendingTransfers,
    fetchAllUsersInfo,
    setActiveNotification,
    setActivePhone,
    setSelectedConversationId
  })

  useEffect(() => {
    if (returnControlToAi) {
      if (aiBlock) toggleAiBlock()
      setReturnControlToAi(false)
      setIsTransferNotification(false)
    }
  }, [returnControlToAi])

  useEffect(() => {
    if (language) {
      i18n.changeLanguage(language)
    }
  }, [language])

  
  useEffect(() => {
    const fetchUserData = async () => {
      if (activePhone) {
        setIsLoadingConversation(true)
        setSelectedConversationId(null)
        
        const selectedUser = myUsers.find((user: any) => user.phone === activePhone)
      setSelectedUserData(selectedUser)
        const fetchData = async () => {
          try {
            const userIdToUse = activeNotification?.userId || selectedUserIdRef.current;
            
            if (userIdToUse) {
              await fetchMessageHistory(activePhone, undefined, userIdToUse);
            }
            
            selectedUserIdRef.current = null;
          } finally {
            setTimeout(() => {
              setIsLoadingConversation(false)
            }, 500)
          }
        }

        await fetchData()
      } else {
        setSelectedUserData(null)
        setMessages([])
        setIsLoadingConversation(false)
      }
    };

    fetchUserData();
  }, [
    activePhone, 
    activeNotification?.userId,
    session
  ])

  useEffect(() => {
    const fetchUserDataFromNotification = async () => {
      if (!activeNotification?.userId || !session) {
        return;
      }
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/user/data/${activeNotification.userId}`,
          {
            method: "GET",
            headers: {
              Authorization: `${session.token_type} ${session.access_token}`,
            },
          }
        );
        
        if (response.ok) {
          const userData = await response.json();
          setSelectedUserData(userData);
        } else {
          console.error('❌ Error obteniendo datos de usuario:', response.status);
        }
      } catch (error) {
        console.error('❌ Error en fetch de usuario:', error);
      }
    };

    fetchUserDataFromNotification();
  }, [activeNotification?.userId, activeNotification?.transferId, session]);

  useEffect(() => {
    if (!selectedUserData) return
    try {
      const userChannel = supabase
        .channel("user-changes")
        .on(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "user",
            filter: `id=eq.${selectedUserData.id}`,
          },
          (payload) => {
            setSelectedUserData(payload.new)
          },
        )
        .subscribe()

      return () => {
        userChannel.unsubscribe()
      }
    } catch (err: any) {
      console.error("Error sending message:", err)
      setError({ message: err.message, code: "OMN-011" })
    }
  }, [selectedUserData])

  const handleSelectUser = (phone: string) => {
    setSelectedConversationId(null)
    setActivePhone(phone)
  }

  const handleSelectUserFromSidebar = useCallback((phone: string, userId?: string | number) => {
    if (userId) {
      selectedUserIdRef.current = userId;
    }
    setSelectedConversationId(null)
    setActivePhone(phone)
  }, [setSelectedConversationId, setActivePhone]);

  const toggleGroup = (group: string) => {
    if (group === "Hoy") return
    setExpandedGroups((prev) => ({
      ...prev,
      [group]: !prev[group],
    }))
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (!target.closest(".lucide-users-round") && !target.closest(".SelectContent")) {
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const onConversationClick = (conv: any) => {
    if (selectedConversationId === conv.id) {
      internalSetConversationId(null)
      setMessages([])
      internalSetLastDate(null)
    } else {
      internalSetConversationId(conv.id)
      setMessages(conv.messages)
      const lastMessage = conv.messages[conv.messages.length - 1]
      internalSetLastDate(lastMessage?.dateSent || null)
    }
  }

  const isMine =
  !activeNotification
    ? true          
    : String(activeNotification.assigned_to || "") === String(session?.user?.id || "");

  const filteredMessages = useMemo(() => {
    if (!messages || !Array.isArray(messages)) return [];
    return messages.filter(shouldRenderMessage);
  }, [messages]);

  useEffect(() => {
    if (activePhone) {
      setSelectedConversationId(null);
      const selectedUser = myUsers.find(
        (user: any) => user.phone === activePhone
      );
      setSelectedUserData(selectedUser);
      if (session && selectedUser) {
        fetchMessageHistory(activePhone, undefined, selectedUser.id);
      }
    } else {
      setSelectedUserData(null);
      setMessages([]);
    }
  }, [
    activePhone,
    myUsers,
    isHistoryPage,
    fetchMessageHistory,
    setSelectedConversationId,
    session,
  ]);

  return (
    <div className="flex h-[calc(100vh-4rem)] bg-zinc-50">
      {error && <ErrorPopup error={error} onClose={() => setError(null)} />}

      <div className="hidden md:block w-[17.75rem] flex-shrink-0 overflow-y-auto">
        <ChatSidebar
          onSelectUser={handleSelectUserFromSidebar}
          usersWithHistory={usersWithHistory}
          loading={loadingUsersWithHistory}
          updateUsersWithHistory={setUsersWithHistory}
          conversationCounts={conversationCounts}
          setConversationCounts={setConversationCounts}
          dateFilter={dateFilter}
          onDateFilterChange={setDateFilter}
        />
      </div>

      <div className="flex-1 flex flex-col">
        <div className="flex justify-between items-center p-4 bg-[#00AAC6] text-white md:hidden">
          <h1 className="text-xl font-bold flex items-center">{t("vet_chat")}</h1>
          <Sheet open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-white hover:bg-green-600">
                {isSidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 bg-zinc-50">
              <ChatSidebar
                onSelectUser={handleSelectUserFromSidebar}
                usersWithHistory={usersWithHistory}
                loading={loadingUsersWithHistory}
                updateUsersWithHistory={setUsersWithHistory}
                conversationCounts={conversationCounts}
                setConversationCounts={setConversationCounts}
                dateFilter={dateFilter}
                onDateFilterChange={setDateFilter}
              />
            </SheetContent>
          </Sheet>
        </div>

        {(isHistoryPage && activePhone) || messages.length > 0 ? (
          <Card className="flex-1 bg-white overflow-hidden">
            <CardContent className="flex flex-col h-full p-0">
              {selectedUserData && (
                <UserHeaderSection
                  selectedUserData={selectedUserData}
                  isHistoryPage={isHistoryPage}
                  setActivePhone={setActivePhone}
                  setViewingNotifications={setViewingNotifications}
                  setIsHistoryPage={setIsHistoryPage}
                  conversationCounts={conversationCounts}
                  activeNotification={activeNotification}
                  selectedConversationLastDate={selectedConversationLastDate}
                />
              )}
              <div className="flex flex-1 overflow-hidden">
                {isHistoryPage && activePhone && (
                  <div className="w-[220px] border-r overflow-y-auto overflow-x-hidden bg-white pt-1 custom-scrollbar">
                    <ConversationGroupList
                      userConversations={userConversations}
                      expandedGroups={expandedGroups}
                      toggleGroup={toggleGroup}
                      selectedConversationId={selectedConversationId}
                      onConversationClick={onConversationClick}
                    />
                  </div>
                )}
                <div className="flex-1 flex flex-col h-full overflow-hidden">
                  {selectedConversationId || !isHistoryPage ? (
                    <>
                      <ChatWindow messages={filteredMessages} isHistoryPage={isHistoryPage} />
                      {!isHistoryPage  && (
                        <div>
                          <MessageInputSection
                            aiBlock={aiBlock}
                            toggleAiBlock={toggleAiBlock}
                            disabled={aiUpdating || aiBlock || selectedUserData?.ai_block_by !== session!.user.id || !isMine}
                            isBusy={aiUpdating}
                            selectedUserData={selectedUserData}
                            onSendMessage={(msg, imageFile, audioFile, documentFile) => handleSendMessage(msg, activePhone, selectedUserData, aiBlock, imageFile, audioFile, documentFile)}
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <EmptyState t={t} />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="flex-1 bg-white flex flex-col items-center justify-center">
            {isLoadingConversation ? (
              <LoadingData t={t} />
            ) : (
              <>
                <MailOpen className="h-16 w-16" stroke="#6F7D95" strokeWidth={1} />
                <p className="text-gray-500 mt-4">{t("no_messages_to_display")}</p>
              </>
            )}
          </Card>
        )}

        <ResolveDialog
          open={resolveDialogOpen}
          setOpen={setResolveDialogOpen}
          closureContext={closureContext}
          setClosureContext={setClosureContext}
          onConfirm={async () => {
            try {
              await handleResolvedChange(true, closureContext)
            } catch (err: any) {
              console.error(err)
              setError({
                message: err.message,
                code: "OMN-008",
              })
            } finally {
              setMessages([])
              setResolveDialogOpen(false)
              setClosureContext("")
              setSelectedConversationId(null)
            }
          }}
          t={t}
        />

        <ReassignDialog
          open={dialogOpen}
          setOpen={setDialogOpen}
          allAssistants={allAssistants}
          sessionUserId={session?.user.id ?? null}
          selectedAssistantId={selectedAssistantId}
          setSelectedAssistantId={setSelectedAssistantId}
          reassignNote={reassignNote}
          setReassignNote={setReassignNote}
          onReassign={reassignNotification}
          t={t}
        />
      </div>
    </div>
  )
}

export default ChatPage
